/**
 * useAiJob — start and track a long-running AI job.
 *
 * Usage:
 *   const { start, jobId, status, stage, progress, partial, result, error, elapsed, cancel } =
 *     useAiJob({ type: 'solution.analyze' });
 *
 *   await start({ instanceId, solutionId });
 *   // → returns when the job is `done` or `failed`, resolves with the result
 *
 *   // The component renders <SolutionAnalysisView partial={partial} status={status} ... />
 *   // and gets live updates as stages complete.
 *
 * Transport:
 *   - POST AI_JOBS to create
 *   - Socket event 'ai-job-updated' for push notifications
 *   - GET AI_JOB(id) polling every 2s as belt-and-braces
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { io as socketIO } from 'socket.io-client';
import { socketAuth } from '../../../../packages/shared/socket';
import API_ENDPOINTS from '../config/api';

const POLL_INTERVAL_MS = 2000;
const TERMINAL = new Set(['done', 'failed', 'cancelled']);

// dedupeKey: a second start while one runs follows the running job instead of
// paying for another (ai_jobs.dedupe_key).
export function useAiJob({ type, dedupeKey } = {}) {
  const [jobId, setJobId] = useState(null);
  // Input the current/last job was started with — consumers that share one
  // hook instance across records use this to check the job is theirs.
  const [jobInput, setJobInput] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | queued | running | done | failed | cancelled
  const [stage, setStage] = useState(null);
  const [progress, setProgress] = useState(null);
  const [partial, setPartial] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const resolverRef = useRef(null); // { resolve, reject } of the start() promise
  const currentJobIdRef = useRef(null);

  // Elapsed tick
  useEffect(() => {
    if (!startedAt || TERMINAL.has(status)) {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
      elapsedRef.current = null;
      return;
    }
    elapsedRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [startedAt, status]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchJob = useCallback(async (id) => {
    try {
      const res = await fetch(API_ENDPOINTS.AI_JOB(id), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const job = await res.json();
      applyJobState(job);
      return job;
    } catch (err) {
      // Polling failure is non-fatal; socket may still arrive
      console.warn('[useAiJob] poll failed:', err.message);
      return null;
    }
  }, []);

  function applyJobState(job) {
    if (!job || job.id !== currentJobIdRef.current) return;
    setStatus(job.status);
    setStage(job.stage);
    setProgress(job.progress);
    if (job.partialResult) setPartial(job.partialResult);
    if (job.result) setResult(job.result);
    if (job.error) setError(job.error);

    if (TERMINAL.has(job.status)) {
      stopPolling();
      const resolver = resolverRef.current;
      if (resolver) {
        resolverRef.current = null;
        if (job.status === 'done') {
          resolver.resolve(job.result);
        } else {
          resolver.reject(new Error(job.error || `Job ${job.status}`));
        }
      }
    }
  }

  const subscribeSocket = useCallback((instanceId) => {
    if (socketRef.current) return;
    const url = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3010';
    // The handshake carries a ticket for this browser's sessions (#1226):
    // the job's instance room takes participate on it.
    const socket = socketIO(url, {
      transports: ['websocket'],
      auth: socketAuth(),
    });
    socketRef.current = socket;
    socket.on('connect', () => {
      socket.emit('join-instance', { instanceId, deviceType: 'ai-job-listener' });
    });
    socket.on('ai-job-updated', (payload) => {
      if (!payload || payload.jobId !== currentJobIdRef.current) return;
      // Push notification — refetch full state (partial_result not in payload)
      fetchJob(payload.jobId);
    });
  }, [fetchJob]);

  // opts.dedupeKey overrides the hook's: one hook instance can follow jobs for
  // different records (one per solution and saved text in Ratkaisut, #1190).
  const start = useCallback(async (input, opts = {}) => {
    if (!type) throw new Error('useAiJob: type is required');
    const key = opts.dedupeKey ?? dedupeKey;
    if (!input?.instanceId) throw new Error('useAiJob: input.instanceId is required');

    // Reset state
    setJobInput(input);
    setStatus('queued');
    setStage(null);
    setProgress(null);
    setPartial(null);
    setResult(null);
    setError(null);
    setStartedAt(Date.now());
    setElapsed(0);

    // PIN-authenticated workshop devices have no Entra session; include their
    // participantId in the body so the backend can attribute the job and
    // grant access. Following the same body-based pattern as add-node etc.
    let participantId = null;
    try {
      participantId = window.localStorage.getItem('participantId');
    } catch { /* private mode */ }

    const res = await fetch(API_ENDPOINTS.AI_JOBS(), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, input, participantId, ...(key ? { dedupeKey: key } : {}) }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.error || `HTTP ${res.status}`;
      setStatus('failed');
      setError(msg);
      throw new Error(msg);
    }
    const { jobId: newJobId } = await res.json();
    setJobId(newJobId);
    currentJobIdRef.current = newJobId;

    // Subscribe to push + start polling fallback
    subscribeSocket(input.instanceId);
    stopPolling();
    pollRef.current = setInterval(() => fetchJob(newJobId), POLL_INTERVAL_MS);

    // Immediate first poll
    fetchJob(newJobId);

    return new Promise((resolve, reject) => {
      resolverRef.current = { resolve, reject };
    });
  }, [type, dedupeKey, subscribeSocket, stopPolling, fetchJob]);

  const cancel = useCallback(async () => {
    if (!currentJobIdRef.current) return;
    try {
      await fetch(API_ENDPOINTS.AI_JOB(currentJobIdRef.current), {
        method: 'DELETE',
        credentials: 'include',
      });
    } catch { /* */ }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [stopPolling]);

  return {
    start, cancel,
    jobId, jobInput, status, stage, progress,
    partial, result, error, elapsed,
    isRunning: status === 'queued' || status === 'running',
    isDone: status === 'done',
    isFailed: status === 'failed' || status === 'cancelled',
  };
}
